// External Libraries
import { useEffect, useState } from 'react';
import { useForm } from 'react-hook-form';
import { useDispatch, useSelector } from 'react-redux';
import { useTranslation } from 'react-i18next';
import Modal from 'react-bootstrap/Modal';
import Button from 'react-bootstrap/Button';

// UI Components
import FormField from '../../components/ui/FormField';
import CheckboxField from '../../components/ui/CheckboxField';
import SubmitButton from '../../components/ui/SubmitButton';
import { getLocalizedTranslation } from '../../../utils/localization';

// Redux Actions
import { duplicateProductFunc } from '../../../store/custom-actions/products-actions/DuplicateProductSlicer';
import { getProductsFunc } from '../../../store/custom-actions/products-actions/GetProductsSlicer';

// Utils
import { getDuplicateTranslation } from '../../../utils/duplicateTranslations';

function DuplicateProductModal({ show, onHide, product }) {
  const { t } = useTranslation();
  const { register, reset, formState: { errors }, handleSubmit, setValue, watch } = useForm();
  const { loading: submitLoading } = useSelector(state => state.duplicateProductSlicer);
  const { data: languageData } = useSelector(state => state.getLanguageSettingsSlicer);
  const dispatch = useDispatch();

  // Get current language
  const currentLang = languageData?.defaultLang?.slug || 'en';

  const [customizeFields, setCustomizeFields] = useState(false);

  // Watch form values
  const watchCustomize = watch('customize_fields', false);

  useEffect(() => {
    if (product) {
      // Get localized title
      const localizedTitle = getLocalizedTranslation(
        product.translations,
        languageData?.defaultLang?.slug
      )?.title || product.title || '';

      setValue('title', `${localizedTitle} (Copy)`);
      setValue('sku', `${product.sku}-COPY-${Date.now()}`);
      setValue('slug', `${product.slug}-copy-${Date.now()}`);
      setValue('price', product.price);
      setValue('stock', product.stock);
      setValue('active', product.active);
      setValue('track_stock', product.track_stock);
      setValue('copy_variants', true);
      setValue('customize_fields', false);
    }
  }, [product, setValue, languageData]);

  const resetForm = () => {
    reset();
    setCustomizeFields(false);
    onHide();
  };

  const onSubmit = async (data) => {
    const { customize_fields, ...submitData } = data;
    
    // If not customizing, send empty object to use defaults
    const payload = customize_fields ? submitData : {};
    
    const result = await dispatch(duplicateProductFunc({ 
      id: product.id, 
      data: payload 
    }));
    
    if (duplicateProductFunc.fulfilled.match(result)) {
      // Refresh products list
      dispatch(getProductsFunc());
      resetForm();
    }
  };

  if (!product) return null;

  const localizedTitle = getLocalizedTranslation(
    product.translations,
    languageData?.defaultLang?.slug
  )?.title || product.title || '';

  return (
    <Modal show={show} onHide={onHide} size="lg">
      <Modal.Header closeButton>
        <Modal.Title>
          <i className="fas fa-copy me-2"></i>
          {getDuplicateTranslation('Duplicate Product', currentLang)}
        </Modal.Title>
      </Modal.Header>
      <Modal.Body>
        <div className="mb-3">
          <div className="alert alert-info">
            <i className="fas fa-info-circle me-2"></i>
            <strong>{getDuplicateTranslation('Original Product', currentLang)}:</strong> {localizedTitle}
          </div>
        </div>

        <form id="duplicate-product-form" onSubmit={handleSubmit(onSubmit)}>
          <CheckboxField
            name="customize_fields"
            label={getDuplicateTranslation('Customize duplicate settings', currentLang)}
            register={register}
            errors={errors}
            onChange={(e) => setCustomizeFields(e.target.checked)}
          />

          {watchCustomize && (
            <div className="mt-4">
              <h6 className="mb-3">{getDuplicateTranslation('Customize New Product', currentLang)}</h6>
              
              <FormField
                label={getDuplicateTranslation('Product Title', currentLang)}
                name="title"
                register={register}
                required={false}
                errors={errors}
                placeholder={getDuplicateTranslation('Enter new product title', currentLang)}
              />

              <div className="row">
                <div className="col-md-6">
                  <FormField
                    label="SKU"
                    name="sku"
                    register={register}
                    required={false}
                    errors={errors}
                    placeholder={getDuplicateTranslation('Enter new SKU', currentLang)}
                  />
                </div>
                <div className="col-md-6">
                  <FormField
                    label="Slug"
                    name="slug"
                    register={register}
                    required={false}
                    errors={errors}
                    placeholder={getDuplicateTranslation('Enter new slug', currentLang)}
                  />
                </div>
              </div>

              <div className="row">
                <div className="col-md-6">
                  <FormField
                    label="Price"
                    name="price"
                    type="number"
                    register={register}
                    required={false}
                    errors={errors}
                    placeholder={getDuplicateTranslation('Enter new price', currentLang)}
                  />
                </div>
                <div className="col-md-6">
                  <FormField
                    label={getDuplicateTranslation('Stock Quantity', currentLang)}
                    name="stock"
                    type="number"
                    register={register}
                    required={false}
                    errors={errors}
                    placeholder={getDuplicateTranslation('Enter stock quantity', currentLang)}
                  />
                </div>
              </div>

              <div className="row">
                <div className="col-md-6">
                  <CheckboxField
                    name="active"
                    label={getDuplicateTranslation('Active Product', currentLang)}
                    register={register}
                    errors={errors}
                  />
                </div>
                <div className="col-md-6">
                  <CheckboxField
                    name="track_stock"
                    label={getDuplicateTranslation('Track Stock', currentLang)}
                    register={register}
                    errors={errors}
                  />
                </div>
              </div>

              <CheckboxField
                name="copy_variants"
                label={getDuplicateTranslation('Copy Product Variants', currentLang)}
                register={register}
                errors={errors}
              />
            </div>
          )}

          {!watchCustomize && (
            <div className="mt-3">
              <div className="alert alert-success">
                <i className="fas fa-check-circle me-2"></i>
                {getDuplicateTranslation('The product will be duplicated with default settings:', currentLang)}
                <ul className="mb-0 mt-2">
                  <li>{getDuplicateTranslation('Title will be:', currentLang)} <strong>{localizedTitle} (Copy)</strong></li>
                  <li>{getDuplicateTranslation('SKU will be:', currentLang)} <strong>{product.sku}-COPY-{Date.now()}</strong></li>
                  <li>{getDuplicateTranslation('All variants and translations will be copied', currentLang)}</li>
                  <li>{getDuplicateTranslation('All other settings will remain the same', currentLang)}</li>
                </ul>
              </div>
            </div>
          )}
        </form>
      </Modal.Body>
      <Modal.Footer>
        <Button variant="secondary" onClick={onHide}>
          {getDuplicateTranslation('Cancel', currentLang)}
        </Button>
        <SubmitButton 
          isLoading={submitLoading} 
          form="duplicate-product-form"
          text={getDuplicateTranslation('Duplicate Product', currentLang)}
        />
      </Modal.Footer>
    </Modal>
  );
}

export default DuplicateProductModal;
